import { useEffect, useMemo, useState } from "react";
import { computeBonuses, type RunOutcome } from "../game/types";
import { Fireworks } from "./Fireworks";
import { PodiumScene } from "./PodiumScene";
import { WinnerPodiumScene } from "./WinnerPodiumScene";
import { startPodiumMusic, fadeOutPodiumMusic, sfx } from "../game/audio";
import { useT } from "../local/i18n";
import type { AvatarConfig } from "@shared/types";

interface Props {
  outcome: RunOutcome;
  hasNext: boolean;
  onPlayAgain: () => void;
  onNext: () => void;
  onHome: () => void;
  onMainMenu?: () => void;
  avatar?: AvatarConfig;
  coinsAfter?: number;
  lifetimeRunsAfter?: number;
  courseMode?: string;
  displayName?: string;
}

/** Player level + XP-to-next derived from coins + lifetime runs. Mirrors the
 *  LocalLanding "Season progress" formula. */
const XP_GOAL = 800;
function deriveLevel(coins: number, lifetimeRuns: number) {
  const xpTotal = coins * 8 + lifetimeRuns * 30;
  const level = 1 + Math.floor(xpTotal / XP_GOAL);
  const xpInLevel = xpTotal % XP_GOAL;
  return { level, xpInLevel, xpTotal };
}

/** End-of-level screen.
 *
 * Layout (no scroll): the 3D character fills the whole screen as a backdrop,
 * with a translucent stats panel pinned to the right edge.
 * Wide screens get a side-by-side feel; narrow phones stack the panel at
 * the bottom (handled by CSS media query).
 */
export function ResultsScreen({
  outcome,
  hasNext,
  onPlayAgain,
  onNext,
  onHome,
  onMainMenu,
  avatar,
  coinsAfter,
  lifetimeRunsAfter,
  courseMode,
  displayName,
}: Props) {
  const t = useT();
  const bonus = computeBonuses(outcome);
  const correct = outcome.wordOutcomes.filter((w) => w.correct).length;
  const total = outcome.wordOutcomes.length;
  const seconds = Math.round(outcome.timeMs / 1000);

  const after = useMemo(() => {
    const coins = coinsAfter ?? bonus.totalCoins;
    const runs = lifetimeRunsAfter ?? 1;
    return deriveLevel(coins, runs);
  }, [coinsAfter, lifetimeRunsAfter, bonus.totalCoins]);
  const before = useMemo(() => {
    const coins = Math.max(0, (coinsAfter ?? bonus.totalCoins) - bonus.totalCoins);
    const runs = Math.max(0, (lifetimeRunsAfter ?? 1) - 1);
    return deriveLevel(coins, runs);
  }, [coinsAfter, lifetimeRunsAfter, bonus.totalCoins]);
  const xpGained = after.xpTotal - before.xpTotal;
  const leveledUp = after.level > before.level;

  useEffect(() => {
    startPodiumMusic();
    // Celebration fanfare when the results screen mounts — only on a
    // genuine finish (not a death / DNF). Plays once and unsubscribes via
    // the effect tear-down.
    if (outcome.finished) sfx.finish();
    // Slow fade out when the player leaves the podium for anywhere else.
    return () => fadeOutPodiumMusic(1800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Staged reveal of stats panel sections so it doesn't all land at once.
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timers = [
      window.setTimeout(() => setStep(1), 300),
      window.setTimeout(() => setStep(2), 650),
      window.setTimeout(() => setStep(3), 1000),
      window.setTimeout(() => setStep(4), 2400),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  // Animated coin counter.
  const [shownCoins, setShownCoins] = useState(0);
  useEffect(() => {
    if (step < 1) return;
    const start = performance.now();
    const dur = 900;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setShownCoins(Math.round(bonus.totalCoins * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step, bonus.totalCoins]);

  // XP bar fill animation.
  const [xpFraction, setXpFraction] = useState(before.xpInLevel / XP_GOAL);
  useEffect(() => {
    if (step < 3) return;
    const start = performance.now();
    const dur = 1300;
    const fromFrac = before.xpInLevel / XP_GOAL;
    const toFrac = leveledUp ? 1 : after.xpInLevel / XP_GOAL;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setXpFraction(fromFrac + (toFrac - fromFrac) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else if (leveledUp) {
        window.setTimeout(() => setXpFraction(after.xpInLevel / XP_GOAL), 600);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step, before.xpInLevel, after.xpInLevel, leveledUp]);

  const headline = outcome.finished
    ? bonus.flawless
      ? t("Perfect run!")
      : t("Finished!")
    : t("Try again");

  return (
    <div className="results2-root results2-fullbleed">
      {outcome.finished && <Fireworks />}
      <div className="results2-bg" aria-hidden />

      {/* Full-bleed 3D character — fills the whole screen behind the stats.
       *  Level 10 survival winners get the dedicated crown ceremony scene
       *  instead of the generic podium. */}
      {avatar && (
        <div className="results2-character-stage" aria-hidden>
          {courseMode === "double-spinner" && outcome.finished ? (
            <WinnerPodiumScene avatar={avatar} displayName={displayName ?? "Winner"} />
          ) : (
            <PodiumScene avatar={avatar} flawless={bonus.flawless} />
          )}
        </div>
      )}
      {courseMode === "double-spinner" && outcome.finished && (
        <div className="winner-banner">
          <strong>{displayName ?? "WINNER"}</strong>
          <em>· WINNER ·</em>
        </div>
      )}

      {/* Big title pinned top-center. */}
      <div className="results2-title-overlay">{headline}</div>

      {/* Right-side stats panel (collapses to bottom on narrow screens). */}
      <div className="results2-side-panel">
        <div className={`results2-coins ${step >= 1 ? "in" : ""}`}>
          <span className="results2-coin-icon">●</span>
          <strong>{shownCoins}</strong>
          <span className="results2-coin-label">{t("coins")}</span>
        </div>

        <div className={`results2-stats ${step >= 2 ? "in" : ""}`}>
          <div className="results2-stat">
            <span>{t("Words")}</span>
            <strong>{correct}/{total}</strong>
          </div>
          <div className="results2-stat">
            <span>{t("Time")}</span>
            <strong>{seconds}s</strong>
          </div>
          <div className="results2-stat">
            <span>{t("Score")}</span>
            <strong>{bonus.totalScore}</strong>
          </div>
        </div>

        <div className={`results2-xp ${step >= 3 ? "in" : ""}`}>
          <div className="results2-xp-row">
            <div className={`results2-level-badge ${step >= 4 && leveledUp ? "pop" : ""}`}>
              {leveledUp && step >= 4 ? after.level : before.level}
            </div>
            <div className="results2-xp-meta">
              <div className="results2-xp-label">
                {t("Level")} {leveledUp && step >= 4 ? after.level : before.level}
                {xpGained > 0 && (
                  <span className={`results2-xp-delta ${step >= 3 ? "in" : ""}`}>+{xpGained} XP</span>
                )}
              </div>
              <div className="results2-xp-bar">
                <div
                  className="results2-xp-fill"
                  style={{ width: `${Math.min(100, xpFraction * 100)}%` }}
                />
              </div>
            </div>
          </div>
          {leveledUp && step >= 4 && (
            <div className="results2-levelup">{t("⭐ LEVEL UP! ⭐")}</div>
          )}
        </div>

        <div className={`results2-actions ${step >= 2 ? "in" : ""}`}>
          {outcome.finished && hasNext && (
            <button className="btn btn-primary btn-big btn-block" onClick={onNext}>
              {t("Next level →")}
            </button>
          )}
          <button className="btn btn-blue btn-big btn-block" onClick={onPlayAgain}>
            {t("↻ Play again")}
          </button>
          <button className="btn btn-ghost btn-block" onClick={onHome}>
            {t("← Pick another level")}
          </button>
          <button className="btn btn-ghost btn-block" onClick={onMainMenu ?? onHome}>
            {t("🏠 Main menu")}
          </button>
        </div>
      </div>
    </div>
  );
}
